import {
  Box,
  Button,
  Divider,
  FormControl,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import Calendar from "../../components/common/Calendar";
import BsModal from "../../components/common/BaseModal";
import SingleSlotsForm from "../../components/features/LmsKitLabel/SingleSlotForm";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addMoreEvents,
  clearAllEvents,
  createSlots,
  deleteSlot,
  fetchAllSlots,
  selectAllEvents,
  selectAllSlots,
  selectSlotLocation,
  selectSlotsLoading,
  setLocation,
} from "../../slices/slotManagement";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import SelectField from "../../components/styled/SelectField";
import DeleteModal from "../../components/common/BaseModal/DeleteModal";
import { convertSlotsToEvents } from "../../utils/generic/function";
import { fetchLocations, selectAllLocation } from "../../slices/common";
import { hasAuthority } from "../../../utils/authorities";
import { AccessDenied } from "../../../pages/access-denied/AccessDenied";
import { setSlugs } from "../../slices/help";
import { SpinnerLoader } from "../../../assets/loader/loader";

const SlotManagement = () => {
  const isLoading = useSelector(selectSlotsLoading);
  const dispatch = useDispatch();
  const LoginData = useSelector((state) => state.loginData);
  const bmr_permissions = LoginData?.bmr_permissions || [];
  const allSlots = useSelector(selectAllSlots) || [];
  const allEvents = useSelector(selectAllEvents);
  const [addSlotsModal, setAddSlotsModal] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const allLocation = useSelector(selectAllLocation);
  const location = useSelector(selectSlotLocation);
  const [pendingLocationValue, setPendingLocationValue] = useState("");
  const [slotType, setSlotType] = useState("all");
  const [deleteData, setDeleteData] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const role_slug = LoginData?.role_slug ;
  const handleLocation = async (value) => {
    await dispatch(setLocation(value));
  };

  const handlePublish = async () => {
    const slotCounts = {};

    allSlots.forEach((item) => {
      const date = item.start.split("T")[0];

      if (slotCounts[date]) {
        slotCounts[date]++;
      } else {
        slotCounts[date] = 1;
      }
    });

    const result = {
      slots: Object.keys(slotCounts).map((date) => ({
        date: date,
        no_of_slots: slotCounts[date],
      })),
    };

    await dispatch(createSlots({ ...result, location_id: location }));
    fetchEvents();
  };

  const fetchEvents = async () => {
    await dispatch(
      fetchAllSlots({ location_id: location, slot_type: slotType })
    );
  };

  const fetchLoaction = async () => {
    const res = await dispatch(fetchLocations());
    if (res?.payload?.data?.items[0]) {
      await dispatch(setLocation(res?.payload?.data?.items[0]?.id));
    }
  };

  const clearAllSlots = async () => {
    await dispatch(clearAllEvents());
    if (pendingLocationValue) {
      await dispatch(setLocation(pendingLocationValue));
    }
    setConfirmClear(false);
  };

  useEffect(() => {
    if (allLocation?.length == 0) {
      fetchLoaction();
    }
    if (allSlots?.length != 0) {
      setConfirmClear(true);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    if (hasAuthority(["add-slot"], bmr_permissions)) {
      dispatch(setSlugs(["manage-bmr-slot"]));
    } else {
      dispatch(setSlugs([""]));
    }
  }, [slotType, location]);

  const handleSlot = async (dateInfo) => {
    const date = new Date(dateInfo.date);

    const formattedDate = moment(date).format("YYYY-MM-DD");

    const sameDaySlots = allSlots?.filter(
      (slot) => moment(slot.start).format("YYYY-MM-DD") === formattedDate
    );

    const nextSlotIndex = sameDaySlots.length + 1; // Increment the index based on existing slots for that day

    const slot = {
      title: `Slot-${nextSlotIndex}`, // Slot title with the incremented index
      start: moment(date).format("YYYY-MM-DDTHH:mm:ss"), // Start time for the slot
      end: moment(date).format("YYYY-MM-DDTHH:mm:ss"), // End time (same as start time)
      type: "Available", // Type of the slot
    };

    await dispatch(addMoreEvents([...allSlots, slot]));
  };

  const formOptions = [
    {
      type: "select",
      name: "location_id",
      placeholder: "Select any location",
      options: allLocation?.items?.map((op) => ({
        value: op.id,
        label: op.name,
      })),
    },
    {
      type: "select",
      name: "slot_type",
      placeholder: "Select any type",
      options: [
        { value: "all", label: "All Slots" },
        { value: "available", label: "Available" },
        { value: "booked", label: "Confirmed" },
        { value: "request", label: "Booked" },
      ],
    },
  ];

  const handleDelete = async () => {
    if (!deleteData?.id) {
      const dateToMatch = new Date(deleteData?.date);
      const dateToMatchISO = dateToMatch.toISOString().split("T")[0];

      const matchingIndex = allSlots?.findIndex((item) => {
        const itemDateISO = new Date(item.start).toISOString().split("T")[0];
        return itemDateISO === dateToMatchISO;
      });
      if (matchingIndex !== -1) {
        const updatedSlots = allSlots.filter(
          (_, index) => index !== matchingIndex
        );
        await dispatch(addMoreEvents(updatedSlots));
      }
    } else {
      await dispatch(deleteSlot(deleteData?.id));
      fetchEvents();
    }
    setDeleteConfirm(false);
    setDeleteData(null);
  };

  return (
    <div className="p-6 rounded-md relative">
      {hasAuthority(["view-slot"], bmr_permissions) ? (
        <div>
          <Box className="flex justify-content-between align-items-center p-[0.8rem] mt-3  bg-white">
            <div>
              <Typography variant="h6">Slot Management</Typography>
            </div>
            <div className="flex gap-2 align-items-center">
              <FormControl variant={"standard"}>
                <div className="w-100 flex gap-2 align-items-center">
                  <SelectField
                    value={location}
                    onChange={(e) => {
                      setPendingLocationValue(e.target.value);
                      if (allSlots?.length != 0) {
                        setConfirmClear(true);
                      } else {
                        handleLocation(e.target.value);
                      }
                    }}
                    sx={{
                      "& .MuiSelect-select span::before": {
                        color: "#808080",
                        content: `"${
                          formOptions[0].placeholder || "-- Select --"
                        }"`,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{formOptions[0].placeholder || "Select"}</em>
                    </MenuItem>
                    {formOptions[0]?.options?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </SelectField>
                  <SelectField
                    value={slotType}
                    onChange={(e) => {
                      setSlotType(e.target.value);
                    }}
                    sx={{
                      "& .MuiSelect-select span::before": {
                        color: "#808080",
                        content: `"${
                          formOptions[1].placeholder || "-- Select --"
                        }"`,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{formOptions[1].placeholder || "Select"}</em>
                    </MenuItem>
                    {formOptions[1].options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </SelectField>
                </div>
              </FormControl>
              <div>
                {hasAuthority(["add-slot"], bmr_permissions) && (
                  <Button
                    fullWidth={true}
                    disabled={!location}
                    className="w-lg"
                    onClick={() => setAddSlotsModal(true)}
                  >
                    Add Multiple Slots
                  </Button>
                )}
              </div>
              <div>
                {hasAuthority(["add-slot"], bmr_permissions) && (
                  <Tooltip>
                    <LoadingButton
                      loading={isLoading}
                      fullWidth={true}
                      disabled={!allSlots || allSlots?.length == 0}
                      className="bg-[#00B278] pl-2 pr-2"
                      sx={{ width: "100px !important" }}
                      onClick={() => handlePublish()}
                    >
                      Publish
                    </LoadingButton>
                  </Tooltip>
                )}
              </div>
            </div>
          </Box>
          <div className="bg-white">
            <Divider className="ml-2 mr-2" />
          </div>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="60vh" // or any suitable height
            >
              <SpinnerLoader />
            </Box>
          ) : (
            <Calendar
              handleAdd={
                location &&
                hasAuthority(["add-slot"], bmr_permissions) &&
                handleSlot
              }
              location={
                allLocation?.items?.find((val) => val.id == location)?.name
              }
              deleteIcon={hasAuthority(["delete-slot"], bmr_permissions)}
              handleDelete={(id, date) => {
                setDeleteData({ id: id, date: date }), setDeleteConfirm(true);
              }}
              allEvents={[...convertSlotsToEvents(allEvents, [], role_slug), ...allSlots,]}
            />
          )}
          <BsModal
            open={addSlotsModal}
            onClose={() => setAddSlotsModal(false)}
            title="Add Slots"
          >
            <SingleSlotsForm onClose={() => setAddSlotsModal(false)} />
          </BsModal>
          <DeleteModal
            open={deleteConfirm || confirmClear}
            setClose={deleteConfirm ? setDeleteConfirm : setConfirmClear}
            title={
              deleteConfirm
                ? "Are you sure you want to Delete ?"
                : "Are you sure you want to Clear ?"
            }
            buttonText={deleteConfirm ? "Delete" : "Clear"}
            handleSuccess={deleteConfirm ? handleDelete : clearAllSlots}
          />
        </div>
      ) : (
        <AccessDenied />
      )}
    </div>
  );
};

export default SlotManagement;
